import React, { useState, useEffect } from 'react';
import weighingService from '@/services/weighing';
import { Pesée } from '@/types';

interface PendingWeighingsProps {
  onSelectWeighing: (weighing: Pesée) => void;
  filterStatus?: string;
}

const PendingWeighings: React.FC<PendingWeighingsProps> = ({ onSelectWeighing, filterStatus }) => {
  const [pendingWeighings, setPendingWeighings] = useState<Pesée[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPendingWeighings();
  }, [filterStatus]);

  const loadPendingWeighings = async () => {
    try {
      setLoading(true);
      setError('');
      
      let weighings: Pesée[] = [];
      
      if (filterStatus) {
        // Filtrer par statut spécifique
        const allWeighings = await weighingService.getPesées();
        
        if (allWeighings.results) {
          weighings = allWeighings.results.filter((weighing: Pesée) => weighing.statut === filterStatus);
        } else {
          setError('Aucune pesée trouvée');
          return;
        }
      } else {
        // Utiliser la méthode par défaut
        const response = await weighingService.getPeséesEnCours();
        
        if (response.success && response.data) {
          weighings = response.data;
        } else {
          setError(response.message || 'Erreur lors du chargement des pesées en cours');
          return;
        }
      }
      
      setPendingWeighings(weighings);
    } catch (err: any) {
      console.error('❌ Erreur lors du chargement:', err);
      setError('Erreur lors du chargement des pesées en cours: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatWeight = (weight: number) => {
    return `${weight.toLocaleString('fr-FR')} kg`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusInfo = (statut: string) => {
    switch (statut) {
      case 'EN_ATTENTE_TARE':
        return {
          label: 'En attente tare',
          color: 'bg-yellow-100 text-yellow-800',
          borderColor: 'border-yellow-200'
        };
      case 'BRUT_ENREGISTRE':
        return {
          label: 'Brut enregistré',
          color: 'bg-green-100 text-green-800',
          borderColor: 'border-green-200'
        };
      case 'EN_COURS':
        return {
          label: 'En cours',
          color: 'bg-blue-100 text-blue-800',
          borderColor: 'border-blue-200'
        };
      default:
        return {
          label: statut,
          color: 'bg-gray-100 text-gray-800',
          borderColor: 'border-gray-200'
        };
    }
  };

  const getProgressPercentage = (weighing: Pesée) => {
    if (weighing.poids_brut && weighing.poids_tare) {
      return 100; // Complété
    } else if (weighing.poids_brut) {
      return 50; // Poids brut enregistré
    } else {
      return 25; // Début
    }
  };

  const getCurrentStateMessage = (weighing: Pesée) => {
    if (weighing.poids_brut && weighing.poids_tare) {
      return 'Pesée complétée - Prêt pour le ticket';
    } else if (weighing.poids_brut) {
      return 'Poids brut enregistré - En attente d\'analyse qualité';
    } else {
      return 'Pesée en cours - Enregistrement du poids brut';
    }
  };

  const filteredWeighings = pendingWeighings.filter(weighing => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      weighing.reference?.toLowerCase().includes(searchLower) ||
      weighing.camion_immatriculation?.toLowerCase().includes(searchLower) ||
      weighing.produit_nom?.toLowerCase().includes(searchLower) ||
      weighing.chauffeur_nom?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          <span className="ml-3 text-gray-600">Chargement des pesées...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Pesées en cours ({filteredWeighings.length})
          </h3>
          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
            {filteredWeighings.length} trouvée(s)
          </span>
        </div>
        <button
          onClick={loadPendingWeighings}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Actualiser"
        >
          <i className="fas fa-sync-alt"></i>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Rechercher par référence, véhicule ou produit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
        />
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {filteredWeighings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <i className="fas fa-inbox text-4xl mb-3 text-gray-300"></i>
          <p className="text-sm">Aucune pesée en cours</p>
          {searchTerm && (
            <p className="text-xs text-gray-400 mt-1">
              Aucun résultat pour "{searchTerm}"
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredWeighings.map((weighing) => {
            const statusInfo = getStatusInfo(weighing.statut);
            const progress = getProgressPercentage(weighing);
            const stateMessage = getCurrentStateMessage(weighing);

            return (
              <div
                key={weighing.id}
                className={`p-4 border ${statusInfo.borderColor} rounded-lg hover:shadow-md transition-all cursor-pointer bg-white`}
                onClick={() => onSelectWeighing(weighing)}
              >
                {/* Header avec référence et date */}
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-gray-900 text-sm">
                    {weighing.reference}
                  </div>
                  <div className="text-xs text-gray-500">
                    {weighing.date_heure ? formatDate(weighing.date_heure) : 'Date non disponible'}
                  </div>
                </div>

                {/* Détails */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Véhicule:</span> {weighing.camion_immatriculation || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">Produit:</span> {weighing.produit_nom || 'N/A'}
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium">Poids:</span> {weighing.poids_brut ? formatWeight(Number(weighing.poids_brut)) : 'N/A'}
                  </div>
                </div>

                {/* Statut et progression */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    Progression {progress}%
                  </span>
                </div>

                {/* Barre de progression */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>

                {/* Message d'état */}
                <div className="text-xs text-gray-600 mb-3">
                  {stateMessage}
                </div>

                {/* Bouton continuer */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectWeighing(weighing);
                  }}
                  className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center"
                >
                  <i className="fas fa-arrow-up mr-2"></i>
                  Continuer
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PendingWeighings;
